import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import { AskAssistantButton } from "../../common/agent/AskAssistantButton";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { isWithoutCompetition } from "../sourcing-events/buyer/wizard/utils/noCompetition";
import { FiChevronRight, FiCornerUpLeft, FiExternalLink, FiFileText } from "react-icons/fi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { EFormType, PublicationChannel, SourcingEventStatus, SourcingEventDto } from "../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { Explanation } from "../../common/support/Explanation/Explanation";
import { urls } from "../../urls";
import {
  formatEstimatedValue,
  getContractNatureLabel,
  getProcedureLabel,
} from "../sourcing-events/portal/utils/displayFormatters";
import { isDpsScheme, isQualificationScheme } from "../sourcing-events/buyer/wizard/utils/procedure";
import { isNegotiationWithoutNotice } from "../sourcing-events/buyer/wizard/utils/negotiationWithoutNotice";
import { LabelValueGrid } from "../sourcing-events/portal/view-single/LabelValueGrid";
import { DevFastForwardMenu } from "./DevFastForwardMenu";
import { useCanManageSourcingEvent } from "./useCanManageSourcingEvent";
import { EventLifecycleMenu } from "./EventLifecycleMenu";
import { SourcingEventStatusTag } from "./SourcingEventStatusTag";
import {
  competitionStage,
  publishedBidDeadline,
  effectiveRequestDeadline,
  type CompetitionStage,
} from "./qualification/stage";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { isAnnouncedPartI } from "../../common/procurement/foaPartDisplay";
import { buildDoffinNoticeUrl } from "./doffinNoticeUrl";
import { RoundPager } from "./rounds/RoundPager";
import { useRoundSelection } from "./rounds/RoundSelectionContext";
import { useNavLabel } from "../../common/layout/useNavLabel";

const DASH = "–";

const personName = (p: { firstName?: string | null; lastName?: string | null; email: string }) =>
  [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;

// t('Interest phase') t('Request phase') t('Qualification phase') t('Tender phase')
const STAGE_LABEL_KEYS: Record<CompetitionStage, string> = {
  INTEREST_OPEN: "Interest phase",
  REQUESTS_OPEN: "Request phase",
  QUALIFICATION: "Qualification phase",
  TENDERING: "Tender phase",
};

const PROTOCOL_STATUSES: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Evaluated,
  SourcingEventStatus.Completed,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Cancelled,
  SourcingEventStatus.Closed,
];

interface Props {
  event: SourcingEventDto;
  onViewPublic?: () => void;
  responsibleEditControl?: ReactNode;
  shareControl?: ReactNode;
}

export const EventHeader = ({ event, onViewPublic, responsibleEditControl, shareControl }: Props) => {
  const { t } = useTranslation();
  const navLabel = useNavLabel();
  const terms = useProcurementTerms();
  const navigate = useNavigate();
  const displayDateWithTime = useDisplayDateWithTime();
  const canManage = useCanManageSourcingEvent(event);
  const canGenerateProtocol = PROTOCOL_STATUSES.includes(event.status);
  const { responsiblePerson } = event;
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const isPrivateEvent = event.eventContext.type === "Private";
  const frameworkAgreementId =
    event.eventContext.type === "MiniCompetition" ? event.eventContext.frameworkAgreementId : null;
  const mayOpenFrameworkAgreement = !!frameworkAgreementId && event.frameworkAgreementLink?.mayOpen === true;
  const value = event.eventContext.estimatedValue;
  const contractNature = event.eventContext.contractNature;
  const doffinReference = publicContext?.publications?.find(
    (p) => p.channel === PublicationChannel.Doffin && p.eFormType === EFormType.Cn16 && p.externalId,
  )?.externalId;
  const stage = competitionStage(event);
  const { selectedRound } = useRoundSelection();
  // With a round selected, the header speaks for that round — its deadline is the frist.
  const bidDeadline = selectedRound?.deadline ?? publishedBidDeadline(event);
  const requestDeadline = effectiveRequestDeadline(event);
  const isTwoStage = stage != null;
  const withoutCompetition = isWithoutCompetition(publicContext?.procedureType);

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
      <Flex
        direction={{ base: "column", md: "row" }}
        wrap={{ md: "wrap" }}
        justify="space-between"
        align={{ base: "stretch", md: "flex-start" }}
        gap="3"
        mb="4"
      >
        <Breadcrumb
          separator={<Icon as={FiChevronRight} color="smMuted" boxSize="3" />}
          fontSize="sm"
          flex={{ md: "1 1 280px" }}
          minW="0"
          overflow="hidden"
          p="1"
          m="-1"
          data-testid="event-header-breadcrumb"
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to={urls.app.sourcingEvents.viewAll} color="smPrimary">
              {navLabel("All procurements")}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color="smMuted" isTruncated maxW={{ base: "220px", md: "360px", lg: "520px" }}>
              {eventData.title || t("Untitled")}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <HStack
          spacing="2"
          flex={{ md: "0 1 auto" }}
          justify={{ base: "flex-start", md: "flex-end" }}
          flexWrap="wrap"
          sx={{ "& > *": { flexShrink: 0 } }}
          data-testid="event-header-actions"
        >
          <AskAssistantButton entityId={event.id} entityName={eventData.title} />
          {shareControl}
          {canManage && <DevFastForwardMenu event={event} />}
          {canManage && <EventLifecycleMenu event={event} />}
          {onViewPublic && (
            <Button size="sm" variant="outline" leftIcon={<Icon as={FiExternalLink} />} onClick={onViewPublic}>
              {t("View as supplier")}
            </Button>
          )}
          {canManage && (
            <HStack spacing="2">
              <Explanation text={t("Complete the evaluation first")} enabled={!canGenerateProtocol}>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Icon as={FiFileText} />}
                  isDisabled={!canGenerateProtocol}
                  onClick={() => navigate(urls.app.sourcingEvents.protocol.url(event.id))}
                >
                  {t("Generate protocol")}
                </Button>
              </Explanation>
              {!canGenerateProtocol && (
                <Text fontSize="sm" color="smMuted">
                  {t("Complete the evaluation first")}
                </Text>
              )}
            </HStack>
          )}
        </HStack>
      </Flex>

      <Stack spacing="2" mb="5">
        <Heading as="h1" size="lg" color="smText" lineHeight="1.15">
          {eventData.title || t("Untitled")}
        </Heading>
        {mayOpenFrameworkAgreement && frameworkAgreementId && (
          <Button
            variant="link"
            size="sm"
            alignSelf="flex-start"
            colorScheme="teal"
            leftIcon={<Icon as={FiCornerUpLeft} />}
            onClick={() => navigate(urls.app.frameworkAgreements.view.go(frameworkAgreementId))}
          >
            {t("Part of a framework agreement")}
          </Button>
        )}
        <HStack spacing="2" align="center" flexWrap="wrap">
          <SourcingEventStatusTag status={event.status} />
          {frameworkAgreementId && (
            <Tag colorScheme="teal" variant="subtle">
              {t("Mini-competition")}
            </Tag>
          )}
          {isPrivateEvent && (
            <Tag colorScheme="gray" variant="subtle">
              {terms.simplifiedRegime}
            </Tag>
          )}
          {stage && event.status === SourcingEventStatus.Published && (
            <Tag colorScheme="purple" variant="subtle">
              {t(STAGE_LABEL_KEYS[stage])}
            </Tag>
          )}
        </HStack>
        <RoundPager justify="flex-start" />
      </Stack>

      <LabelValueGrid
        rows={[
          {
            label: t("Responsible buyer"),
            value: (
              <HStack spacing="2" flexWrap="wrap">
                <Text as="span">{responsiblePerson ? personName(responsiblePerson) : DASH}</Text>
                {responsibleEditControl}
              </HStack>
            ),
          },
          ...(isTwoStage &&
          !isQualificationScheme(publicContext?.procedureType) &&
          !isNegotiationWithoutNotice(publicContext?.procedureType)
            ? [
                {
                  label: isDpsScheme(publicContext?.procedureType)
                    ? t("Deadline for the first intake")
                    : t("Deadline for requests to participate"),
                  value: requestDeadline ? displayDateWithTime(requestDeadline) : DASH,
                },
              ]
            : []),
          ...(withoutCompetition
            ? []
            : [
                {
                  label: t("Deadline"),
                  value: bidDeadline ? displayDateWithTime(bidDeadline) : DASH,
                },
              ]),
          ...(publicContext && !withoutCompetition
            ? [
                {
                  label: t("Question deadline"),
                  value: event.questionDeadline ? displayDateWithTime(event.questionDeadline) : DASH,
                },
              ]
            : []),
          ...(publicContext
            ? [
                {
                  label: t("Procedure"),
                  value: publicContext.procedureType
                    ? getProcedureLabel(publicContext.procedureType, t, {
                        namesNoProcedure: isAnnouncedPartI(publicContext),
                      })
                    : DASH,
                },
              ]
            : []),
          ...(publicContext
            ? [
                {
                  label: t("Contract type"),
                  value: contractNature ? getContractNatureLabel(contractNature, t) : DASH,
                },
                {
                  label: t("Estimated value"),
                  value: value?.value != null ? formatEstimatedValue(value.value, value.currency) : DASH,
                },
              ]
            : []),
          ...(doffinReference
            ? [
                {
                  label: t("Doffin reference"),
                  value: (
                    <Link
                      href={buildDoffinNoticeUrl(doffinReference)}
                      isExternal
                      rel="noopener noreferrer"
                      color="smPrimary"
                      display="inline-flex"
                      alignItems="center"
                      gap="1"
                    >
                      {doffinReference}
                      <Icon as={FiExternalLink} boxSize="3" />
                    </Link>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Box>
  );
};
